import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import Tooltip from '@mui/material/Tooltip'
import {
  CommunityResource,
  CompetitionEntryResource,
  EventKind
} from '@rallycry/api-suite-typescript/dist/models'
import { useEffect, useRef } from 'react'
import { last } from 'lodash-es'
import { TeamCountDisplay } from '../registration/TeamCountDisplay'
import {
  RcTableBodyRow,
  RcTableCell,
  RcTableStickyBodyCell
} from '../../../../molecules/text/RcTable'
import { isActiveCheckinIssue } from '../alerts/useCompetitionAlertsCheckin'
import { TeamIssueTooltip } from '../registration/TeamIssueTooltip'
import { TeamNoteIcon } from '../team/note/TeamNoteIcon'
import { useCompetition } from '@/entity/competition/useCompetition'
import { useCompetitionTeam } from '@/entity/competition-team/useCompetitionTeam'
import { useOnScreen } from '@/core/hooks/useOnScreen'
import { ErrorCode } from '@/core/error-code'
import { TeamAvatar } from '@/components/organisms/avatar/TeamAvatar'
import { StyledCheckbox } from '@/components/molecules/input/StyledCheckbox'
import { RcIcon } from '@/components/atoms/RcIcon'
import { useCompetitionEvents } from '@/entity/competition/useCompetitionEvents'
import { expand, ExpansionType } from '@/core/expand'
import { RouteParamOverrideProvider } from '@/components/providers/site/RouteParamOverrideProvider'
import Box from '@mui/material/Box'
import { RcSuspense } from '@/components/atoms/RcSuspense'

export const TeamsAdminTableRow = ({
  team,
  lastValidated,
  hasCheckins,
  hasForms,
  isSelected,
  isPreviousSelected,
  isNextSelected,
  isFiltered,
  onSelected,
  onRefresh
}: {
  team: CompetitionEntryResource
  lastValidated: Date
  hasCheckins: boolean
  hasForms: boolean
  isSelected?: boolean
  isPreviousSelected: boolean
  isNextSelected?: boolean
  isFiltered?: boolean
  onSelected: () => any
  onRefresh?: () => any
}) => {
  const { settings, isCompetitionModerator } = useCompetition()
  const { events } = useCompetitionEvents()
  const ref = useRef<HTMLDivElement>()
  const scrolled = useOnScreen({ ref, rootMargin: '50px', setOnce: true })
  const currentOrPreviousCheckins =
    events([EventKind.CHECK_IN]).allCurrentOrPrevious?.map(it => it.id!) || []

  const mostRecentCheckin = last(currentOrPreviousCheckins)
  const community = expand<CommunityResource>(
    team,
    team._expanded,
    ExpansionType.CompetitionCommunity
  )

  const {
    team: teamData,
    read: { isLoading, mutate }
  } = useCompetitionTeam({
    paused: !scrolled,
    expand: '_issues',
    idOrKey: team?.id,
    suspense: false,
    mutable: true,
    dedupingInterval: 15000
  })

  useEffect(() => {
    if (isSelected) mutate()
  }, [lastValidated, isSelected, mutate])

  const isDropped = !!teamData?._issues?.find(
    it => it.code === ErrorCode.DroppedEntry
  )

  return (
    <RcTableBodyRow
      onClick={onSelected}
      selected={isSelected}
      isPreviousSelected={isPreviousSelected}
      isNextSelected={isNextSelected}
      isFiltered={isFiltered}
      sx={{ cursor: 'pointer' }}
    >
      <RcTableStickyBodyCell ref={ref} isSelected={isSelected}>
        <StyledCheckbox checked={isSelected} />
        <TeamAvatar
          team={team}
          community={community}
          isDegraded={isDropped}
          modal
          onRefresh={onRefresh}
          badgeContent={
            isDropped ? (
              <Tooltip title='Team Dropped'>
                <span>
                  <FontAwesomeIcon
                    size='sm'
                    icon={['fal', 'sign-in']}
                    style={{
                      color: '#FFF',
                      transform: 'rotate(180deg)'
                    }}
                  />
                </span>
              </Tooltip>
            ) : null
          }
        />
      </RcTableStickyBodyCell>
      {isCompetitionModerator ? (
        <RcTableCell>
          {team?.note?.id && isCompetitionModerator ? (
            <RouteParamOverrideProvider
              context={{ teamId: team?.id?.toString() }}
            >
              <TeamNoteIcon onComplete={onRefresh} />
            </RouteParamOverrideProvider>
          ) : null}
        </RcTableCell>
      ) : null}
      {settings?.teamSizeMin && settings?.teamSizeMax ? (
        <RcTableCell>
          <TeamCountDisplay
            current={team._membersTotalElements}
            min={settings?.teamSizeMin}
            max={settings?.teamSizeMax}
            fancy
          />
        </RcTableCell>
      ) : null}
      {hasCheckins ? (
        <RcTableCell>
          {isLoading || !teamData ? (
            <Skeleton variant='rounded' animation={false} />
          ) : (
            <TeamIssueTooltip
              teamId={teamData?.id!}
              skipLink
              display={
                teamData?._issues?.find(it =>
                  isActiveCheckinIssue(it, mostRecentCheckin)
                ) ? (
                  <Chip
                    color='error'
                    label={<RcIcon icon={['fal', 'times']} ml={0.5} mr={0.5} />}
                  />
                ) : (
                  <Chip
                    color='success'
                    label={<RcIcon icon={['fal', 'check']} />}
                  />
                )
              }
            />
          )}
        </RcTableCell>
      ) : null}
      {hasForms ? (
        <RcTableCell>
          {isLoading || !teamData ? (
            <Skeleton variant='rounded' animation={false} />
          ) : (
            <TeamIssueTooltip
              teamId={teamData?.id!}
              skipLink
              display={
                teamData?._issues?.find(
                  it => it.code === ErrorCode.FormMissingResponse
                ) ? (
                  <Chip
                    color='error'
                    label={<RcIcon icon={['fal', 'times']} ml={0.5} mr={0.5} />}
                  />
                ) : (
                  <Chip
                    color='success'
                    label={<RcIcon icon={['fal', 'check']} />}
                  />
                )
              }
            />
          )}
        </RcTableCell>
      ) : null}
      {settings?.requiredNetwork ? (
        <RcTableCell>
          {isLoading || !teamData ? (
            <Skeleton variant='rounded' animation={false} />
          ) : (
            <TeamIssueTooltip
              teamId={teamData?.id!}
              skipLink
              display={
                teamData?._issues?.find(
                  it => it.code === ErrorCode.MissingRequiredNetwork
                ) ? (
                  <Chip
                    color='error'
                    label={<RcIcon icon={['fal', 'times']} ml={0.5} mr={0.5} />}
                  />
                ) : (
                  <Chip
                    color='success'
                    label={<RcIcon icon={['fal', 'check']} />}
                  />
                )
              }
            />
          )}
        </RcTableCell>
      ) : null}
      <RcTableCell>{team.seed}</RcTableCell>
    </RcTableBodyRow>
  )
}
